import { useEffect } from "react";

/**
 * Adds an `is-visible` class to every `.reveal` element when it scrolls
 * into view. Also watches for new `.reveal` nodes (e.g. after filtering)
 * so dynamically added cards still animate in.
 */
export function useReveal() {
  useEffect(() => {
    const supportsIO = "IntersectionObserver" in window;
    let observer: IntersectionObserver | null = null;

    const show = (el: HTMLElement) => {
      el.classList.add("is-visible");
    };

    if (supportsIO) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              show(entry.target as HTMLElement);
              observer?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
      );
    }

    const register = (node: Element) => {
      const el = node as HTMLElement;
      if (el.classList.contains("reveal") && !el.classList.contains("is-visible")) {
        if (observer) observer.observe(el);
        else show(el);
      }
      el.querySelectorAll?.(".reveal:not(.is-visible)").forEach((child) => {
        if (observer) observer.observe(child);
        else show(child as HTMLElement);
      });
    };

    Array.from(document.querySelectorAll(".reveal")).forEach(register);

    const mutation = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) register(node as Element);
        });
      });
    });
    mutation.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      mutation.disconnect();
    };
  }, []);
}

export default useReveal;